import {
  ApplicationInsights,
  SeverityLevel,
} from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

const TelemetryLevel = {
  ErrorsOnly: 0,
  WarningsAndErrors: 1,
  Verbose: 2,
};

const STORAGE_KEY = "appinsights:logLevel";

let _cachedLevel = null;

const getTelemetryLevel = () => {
  if (_cachedLevel !== null) return _cachedLevel;

  const raw = localStorage.getItem(STORAGE_KEY);
  const requested = raw !== null ? Number(raw) : TelemetryLevel.ErrorsOnly;

  if (requested === TelemetryLevel.Verbose) {
    console.warn(
      "[AppInsights] Telemetry level is set to Verbose. " +
        "This may generate high-volume logging. Remember to reset to ErrorsOnly after debugging."
    );
    _cachedLevel = TelemetryLevel.Verbose;
    return _cachedLevel;
  }

  if (requested === TelemetryLevel.WarningsAndErrors) {
    console.warn(
      "[AppInsights] Telemetry level is set to Warnings and Errors. " +
        "This may generate high-volume logging. Remember to reset to ErrorsOnly after debugging."
    );
    _cachedLevel = TelemetryLevel.WarningsAndErrors;
    return _cachedLevel;
  }

  _cachedLevel = TelemetryLevel.ErrorsOnly;
  return _cachedLevel;
};

export const resetTelemetryLevel = () => {
  _cachedLevel = null;
};

window.addEventListener("storage", (e) => {
  if (e.key === STORAGE_KEY) resetTelemetryLevel();
});

const reactPlugin = new ReactPlugin();

const appInsights = new ApplicationInsights({
  config: {
    connectionString: window["_env"]?.APPINSIGHTS,
    extensions: [reactPlugin],
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    disableFetchTracking: false,
    disableAjaxTracking: false,
    disableTelemetry: !window["_env"]?.APPINSIGHTS,
  },
});

if (window["_env"]?.APPINSIGHTS) {
  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer((env) => {
    env.tags = env.tags || {};
    env.tags["ai.cloud.role"] = "Kiosk";
  });

  appInsights.addTelemetryInitializer((item) => {
    const level = getTelemetryLevel();
    switch (item.baseType) {
      case "ExceptionData":
        return true;

      case "TraceData": {
        const severity =
          item.baseData?.severityLevel ?? SeverityLevel.Information;

        if (level === TelemetryLevel.ErrorsOnly) {
          return severity >= SeverityLevel.Error;
        }

        if (level === TelemetryLevel.WarningsAndErrors) {
          return severity >= SeverityLevel.Warning;
        }

        return true;
      }

      case "PageViewData":
      case "RemoteDependencyData":
      case "RequestData":
        return level === TelemetryLevel.Verbose;

      default:
        return false;
    }
  });
}

export { reactPlugin, appInsights };
